// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { patch, updateItem } from '@ngxs/store/operators';

// Services
import { CoreService } from '../../../../shared/services/core.service';
import { BopService } from '../services/bop.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Models
import { AssetViewModel } from '../../string/models/string-view.model';
import { AppCleanup } from '../../../../shared/models';

// Store
import { BopActions } from './bop.action';

// Enums
import { StatusType } from '../../../../shared/enums';

export interface BopStateModel {
	items: AssetViewModel[];
	loaded: boolean;
}

const defaultState: BopStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'bop',
	defaults: defaultState,
})
@Injectable()
export class BopState extends AppCleanup {
	constructor(
		private bopService: BopService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(BopActions.GetAll)
	getAll(ctx: StateContext<BopStateModel>, action: BopActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.bopService
			.getBops()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (bops) => {
					ctx.patchState({
						items: bops,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.BOP.TITLE') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(BopActions.SetAll)
	setAll(ctx: StateContext<BopStateModel>, action: BopActions.SetAll) {
		ctx.patchState({
			items: action.bops,
			loaded: true,
		});
	}

	@Action(BopActions.Update)
	update(ctx: StateContext<BopStateModel>, action: BopActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.bopService
			.updateBop(action.data.bop)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (bop) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === bop.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((b) => b.id === bop.id, bop),
							})
						);
					}

					ctx.dispatch(new BopActions.UpdateSuccess(bop));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.BOP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new BopActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.BOP.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Selector()
	static isLoaded(state: BopStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static bops(state: BopStateModel): AssetViewModel[] {
		return state.items;
	}
}
